import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Button } from '@/components/ui/Button';
import { GiftLoader } from '@/components/GiftLoader';
import { completeSignIn, useAuth } from '@/lib/auth';

/**
 * Landing point for AuthKit's redirect. WorkOS sends:
 *   /auth/callback?code=…&state=…   (success)
 *   /auth/callback?error=…&error_description=…   (user cancelled, etc.)
 *
 * We exchange the code via /api/auth/callback, persist the session, then
 * redirect to whatever path the user originally tried to reach.
 */
export function AuthCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const code = params.get('code');
    const state = params.get('state');
    const oauthError = params.get('error');

    if (oauthError) {
      setError(params.get('error_description') ?? oauthError);
      return;
    }
    if (!code || !state) {
      setError('Missing authorization code. Please start sign-in again.');
      return;
    }

    let cancelled = false;
    completeSignIn(code, state)
      .then(({ session, returnTo }) => {
        if (cancelled) return;
        setSession(session);
        navigate(returnTo, { replace: true });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Sign-in failed.');
      });
    return () => {
      cancelled = true;
    };
  }, [params, navigate, setSession]);

  return (
    <section className="flex-1 flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md text-center">
        {error ? (
          <>
            <h1 className="font-display text-2xl font-semibold mb-3">
              Sign-in didn't complete
            </h1>
            <p className="text-ink-muted mb-6">{error}</p>
            <Button onClick={() => navigate('/', { replace: true })}>
              Back to home
            </Button>
          </>
        ) : (
          <GiftLoader caption="Signing you in…" />
        )}
      </div>
    </section>
  );
}
